import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'

import { PkgShippingMethod } from '@/api/user-package/types/UserPkg'
import { Divider } from '@/Components/Divider'
import { GrowPaymentIframe } from '@/Components/GrowPaymentIframe'
import ClockIcon from '@/Components/icons/clock.svg?react'
import CostIcon from '@/Components/icons/cost.svg?react'
import PackageIcon from '@/Components/icons/package.svg?react'
import SuccessIcon from '@/Components/icons/success.svg?react'
import { Button } from '@/Components/ui/button'
import { Card, CardContent } from '@/Components/ui/card'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { RoutePath } from '@/RoutePath'

import { ShippingOptions } from './ShippingPage'
const shippingMethodLabels = {
  [PkgShippingMethod.DoorToDoor]: 'משלוח עד הבית',
  [PkgShippingMethod.PickUpPoint]: 'איסוף עצמי מנקודת חלוקה',
}
interface Props {
  shippingOptions: ShippingOptions
  paymentUrl: string
  pkgId: string
  totalPrice: number
}

export default function ProcessPage({ shippingOptions, paymentUrl, pkgId, totalPrice }: Props) {
  const history = useHistory()
  const queryClient = useQueryClient()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const callbackPrice = searchParams.get('callbackPrice')
  const [showSuccessDialog, setShowSuccessDialog] = useState(!!callbackPrice)

  useEffect(() => {
    if (!paymentUrl && !callbackPrice) {
      history.push(`${RoutePath.PaymentSummary}/${pkgId}`)
    }
  }, [history, pkgId, paymentUrl, callbackPrice])

  const onClose = async () => {
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PACKAGES] })
    setShowSuccessDialog(false)
    history.push(RoutePath.ClientPkg)
  }

  return (
    <div className='h-[calc(100dvh-var(--navbar-height)-120px)]'>
      {/* Payment Iframe */}
      {!showSuccessDialog && (
        <GrowPaymentIframe
          paymentUrl={paymentUrl}
          onSuccess={() => setShowSuccessDialog(true)}
          onError={() => history.push(`${RoutePath.PaymentSummary}/${pkgId}`)}
          onClose={() => history.push(`${RoutePath.PaymentSummary}/${pkgId}`)}
        />
      )}

      {/* Success Dialog */}
      {showSuccessDialog && (
        <Card>
          <CardContent className='flex flex-col items-center gap-4'>
            {/* Success Icon */}
            <div className='h-6 w-6'>
              <SuccessIcon className='text-icon-primary h-full w-full' />
            </div>

            {/* Title and Description */}
            <div className='flex flex-col items-center gap-4'>
              <h2 className='text-text-primary text-center text-base font-medium'>החבילה בדרך אליך!</h2>
              <p className='text-text-secondary text-center text-sm'>
                איזה כיף!
                <br />
                ההזמנה התקבלה, והחבילה תצא אליך בקרוב.
                <br />
                תוכל לעקוב אחריה בטאב ׳חבילות שנשלחו׳
              </p>
            </div>

            {/* Info Container */}
            <div className='bg-button-primary-selected-segment w-full rounded-lg p-4'>
              <div className='flex flex-col gap-2'>
                {/* Cost Info */}
                <div className='flex items-center gap-2'>
                  <CostIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>עלות משלוח כולל: ${callbackPrice ?? totalPrice}</span>
                </div>

                {/* Package Count */}
                <div className='flex items-center gap-2'>
                  <PackageIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>
                    סוג המשלוח: {shippingMethodLabels[shippingOptions.selectedShippingMethod]}
                  </span>
                </div>

                {/* Time Info */}
                <div className='flex items-center gap-2'>
                  <ClockIcon className='text-text-secondary size-4' />
                  <span className='text-text-secondary text-sm'>המשלוח צפוי להגיע בתוך 14-21 ימי עסקים</span>
                </div>
              </div>
            </div>
            <Divider />
            <Link to={RoutePath.ClientPkg}>
              <Button size='sm' variant='ghost' onClick={onClose} className='text-text-link text-sm'>
                חזור לחבילות שלי
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
